import React, { useCallback, useState, useRef, useEffect } from 'react';
import { Subscribe } from 'unstated';
import { useIntl, defineMessages } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Text } from '@atlaskit/primitives/compiled/text';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import Lozenge from '@atlaskit/lozenge/lozenge';

import { manuallyLogExpExposure } from '@confluence/feature-experiments';
import { usePageSpaceKey } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { useFetch3PAuthentications } from './hooks/use3PAuthentication.ts';
import { ReviewContentSuggestionsModal } from './ReviewContentSuggestionsModal.tsx';
import {
	useFetchSuggestionCount,
	useFetchSuggestionCountFromFragment,
	type RemoteObjectSuggestionCountFragmentRef,
	type UseFetchSuggestionCountResult,
} from './hooks/useFetchSuggestionCount.ts';
import { StackedContentIcons } from './StackedContentIcons.tsx';
import { EXTERNAL_PRODUCT_CONNECTION } from './__types__/RemoteObject.ts';
import { ThirdPartyAuthentication } from './ThirdPartyAuthentication.tsx';
import { CloseMenu } from './CloseMenu.tsx';
import { useDismissCuration } from './hooks/useDismissCuration.ts';
import {
	PageTreeForceFetchSubscriber,
	type ForceFetchPageIds,
} from './PageTreeForceFetchSubscriber.tsx';

const styles = cssMap({
	container: {
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.small'),
		marginTop: token('space.100'),
		overflow: 'hidden',
		padding: token('space.200'),
		position: 'relative',
	},
	topRow: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		marginBottom: token('space.050'),
	},
	iconsWrapper: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
	},
	mainAction: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: token('space.050'),
		backgroundColor: 'transparent',
		padding: token('space.0'),
		width: '100%',
		textAlign: 'start',
	},
});

const i18n = defineMessages({
	addContent: {
		id: 'space-curation.space-navigation.related-content.add-content',
		defaultMessage: 'Add suggested content from your connected work apps',
		description: 'Title for the suggestion entry point in the space navigation',
	},
	beta: {
		id: 'space-curation.space-navigation.related-content.beta',
		defaultMessage: 'Beta',
		description: 'Beta lozenge label displayed next to the suggestion entry point',
	},
	betaUpdated: {
		id: 'space-curation.space-navigation.related-content.beta.updated',
		defaultMessage: 'Beta',
		description: 'Sentence case Beta lozenge label displayed next to the suggestion entry point',
	},
	addContentAction: {
		id: 'space-curation.space-navigation.related-content.add-content-action',
		defaultMessage: 'Review and add',
		description: 'Label for the button inside the suggestion entry point card',
	},
	remindTomorrowSuccessTitle: {
		id: 'space-curation.space-navigation.related-content.remind-tomorrow.success-title',
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		defaultMessage: "Got it, we'll check back later",
		description:
			'Title of the success flag shown after the user chooses to be reminded about content suggestions tomorrow',
	},
	remindTomorrowSuccessDescription: {
		id: 'space-curation.space-navigation.related-content.remind-tomorrow.success-description',
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		defaultMessage: "We'll remind you about these suggestions again in 24 hours.",
		description:
			'Description of the success flag shown after the user chooses to be reminded about content suggestions tomorrow',
	},
	neverShowAgainSuccessTitle: {
		id: 'space-curation.space-navigation.related-content.never-show-again.success-title',
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		defaultMessage: "We won't show this again",
		description:
			'Title of the success flag shown after the user chooses to never see content suggestions in this space again',
	},
	neverShowAgainSuccessDescription: {
		id: 'space-curation.space-navigation.related-content.never-show-again.success-description',
		defaultMessage: 'You will no longer see curation suggestions in this space.',
		description:
			'Description of the success flag shown after the user chooses to never see content suggestions in this space again',
	},
	dismissErrorTitle: {
		id: 'space-curation.space-navigation.related-content.dismiss.error-title',
		defaultMessage: 'Something went wrong',
		description: 'Title of the error flag shown when dismissing content suggestions fails',
	},
	dismissErrorDescription: {
		id: 'space-curation.space-navigation.related-content.dismiss.error-description',
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		defaultMessage: "We couldn't update your preference. Please try again.",
		description: 'Description of the error flag shown when dismissing content suggestions fails',
	},
	undoAction: {
		id: 'space-curation.space-navigation.related-content.never-show-again.undo-action',
		defaultMessage: 'Undo',
		description:
			'Label for the link on the success flag that lets the user reverse the "never show again" choice for content suggestions',
	},
});

type SpaceNavigationRelatedContentProps = {
	spaceId?: string;
	suggestionCountFragmentRef?: RemoteObjectSuggestionCountFragmentRef | null;
};

type SpaceNavigationRelatedContentBodyProps = {
	spaceKey: string;
	mountTimestamp: number;
	suggestionCount: number;
	onDismiss: () => void;
	onNeverShowAgain: () => void;
	onModalClose: (hadRejections: boolean) => void | Promise<void>;
	onSaveSuccessRefetch: () => void;
	spaceId?: string;
};

const SpaceNavigationRelatedContentBody = ({
	spaceKey,
	mountTimestamp,
	suggestionCount,
	onDismiss,
	onNeverShowAgain,
	onModalClose,
	onSaveSuccessRefetch,
	spaceId,
}: SpaceNavigationRelatedContentBodyProps) => {
	const intl = useIntl();
	const [modalOpenTimestamp, setModalOpenTimestamp] = useState<number | null>(null);
	const isModalOpen = modalOpenTimestamp !== null;
	const forceFetchPageIdsRef = useRef<ForceFetchPageIds | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const mountValuesRef = useRef({
		spaceId,
		spaceKey,
		suggestionCount,
		mountTimestamp,
		createAnalyticsEvent,
	});

	useEffect(() => {
		const {
			spaceId: si,
			spaceKey: sk,
			suggestionCount: sc,
			mountTimestamp: mt,
			createAnalyticsEvent: cae,
		} = mountValuesRef.current;
		cae({
			type: 'sendUIEvent',
			data: {
				action: 'rendered',
				actionSubject: 'curationEntry',
				source: 'containerNavigation',
				attributes: {
					spaceId: si,
					spaceKey: sk,
					entryPoint: 'suggestionsEntry',
					suggestionCount: sc,
					renderLatencyMs: Date.now() - mt,
				},
			},
		}).fire();
	}, []);

	const handleDismiss = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'curationEntry',
				source: 'containerNavigation',
				attributes: {
					spaceId,
					spaceKey,
					entryPoint: 'suggestionsEntry',
					dismissMethod: 'close_button',
					suggestionCountAtDismiss: suggestionCount,
				},
			},
		}).fire();
		onDismiss();
	};

	const handleNeverShowAgain = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'curationEntry',
				source: 'containerNavigation',
				attributes: {
					spaceId,
					spaceKey,
					entryPoint: 'suggestionsEntry',
					dismissMethod: 'never_show_again',
					suggestionCountAtDismiss: suggestionCount,
				},
			},
		}).fire();
		onNeverShowAgain();
	};

	const handleOpenModal = () => {
		setModalOpenTimestamp(Date.now());
	};
	const handleForceFetchPageIdsChange = useCallback(
		(forceFetchPageIds: ForceFetchPageIds | null) => {
			forceFetchPageIdsRef.current = forceFetchPageIds;
		},
		[],
	);

	return (
		<>
			<Box xcss={styles.container} testId="space-navigation-related-content">
				<Box xcss={styles.topRow}>
					<Box xcss={styles.iconsWrapper}>
						<StackedContentIcons variant="medium" icons={['loom', 'google-drive', 'onedrive']} />
					</Box>
					<CloseMenu onDismiss={handleDismiss} onNeverShowAgain={handleNeverShowAgain} />
				</Box>
				<Pressable xcss={styles.mainAction} onClick={handleOpenModal}>
					<Text>
						{intl.formatMessage(i18n.addContent)}{' '}
						<Lozenge appearance="accent-gray">{intl.formatMessage(i18n.betaUpdated)}</Lozenge>
					</Text>
					<Text color="color.link" weight="medium">
						{intl.formatMessage(i18n.addContentAction)}
					</Text>
				</Pressable>
			</Box>
			<ModalTransition>
				{isModalOpen && modalOpenTimestamp !== null && (
					<ReviewContentSuggestionsModal
						spaceKey={spaceKey}
						spaceId={spaceId}
						modalOpenTimestamp={modalOpenTimestamp}
						onClose={async (hadRejections: boolean) => {
							setModalOpenTimestamp(null);
							await onModalClose(hadRejections);
						}}
						onSaveSuccess={(acceptedParentIds: number[]) => {
							setModalOpenTimestamp(null);
							onSaveSuccessRefetch();
							forceFetchPageIdsRef.current?.(acceptedParentIds);
						}}
					/>
				)}
			</ModalTransition>
			<PageTreeForceFetchSubscriber onForceFetchPageIdsChange={handleForceFetchPageIdsChange} />
		</>
	);
};

export const SpaceNavigationRelatedContent = ({
	spaceId,
	suggestionCountFragmentRef,
}: SpaceNavigationRelatedContentProps): React.JSX.Element =>
	suggestionCountFragmentRef ? (
		<SpaceNavigationRelatedContentFromFragment
			spaceId={spaceId}
			suggestionCountFragmentRef={suggestionCountFragmentRef}
		/>
	) : (
		<LegacySpaceNavigationRelatedContent spaceId={spaceId} />
	);

const LegacySpaceNavigationRelatedContent = ({ spaceId }: { spaceId?: string }) => {
	const [spaceKey] = usePageSpaceKey();
	const { cloudId } = useSessionData();
	const suggestionCountResult = useFetchSuggestionCount({ spaceKey, cloudId });

	return (
		<SpaceNavigationRelatedContentInner
			spaceId={spaceId}
			spaceKey={spaceKey}
			cloudId={cloudId}
			suggestionCountResult={suggestionCountResult}
		/>
	);
};

const SpaceNavigationRelatedContentFromFragment = ({
	spaceId,
	suggestionCountFragmentRef,
}: {
	spaceId?: string;
	suggestionCountFragmentRef: RemoteObjectSuggestionCountFragmentRef;
}) => {
	const [spaceKey] = usePageSpaceKey();
	const { cloudId } = useSessionData();
	const suggestionCountResult = useFetchSuggestionCountFromFragment({
		spaceKey,
		cloudId,
		suggestionCountFragmentRef,
	});

	return (
		<SpaceNavigationRelatedContentInner
			spaceId={spaceId}
			spaceKey={spaceKey}
			cloudId={cloudId}
			suggestionCountResult={suggestionCountResult}
		/>
	);
};

const ThirdPartyAuthenticationEntry = ({
	cloudId,
	spaceId,
	mountTimestamp,
}: {
	cloudId: string;
	spaceId?: string;
	mountTimestamp: number;
}): React.JSX.Element | null => {
	const { unauthenticatedServices, isLoading, refetch, markOptimisticallyAuthenticated } =
		useFetch3PAuthentications({
			products: [EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE, EXTERNAL_PRODUCT_CONNECTION.MICROSOFT],
			cloudId,
		});

	if (isLoading || Object.keys(unauthenticatedServices).length === 0) {
		return null;
	}

	manuallyLogExpExposure('confluence_autonomous_space_curation');
	return (
		<ThirdPartyAuthentication
			unauthenticatedServices={unauthenticatedServices}
			onThirdPartyAuthRefetch={refetch}
			onAuthSuccess={markOptimisticallyAuthenticated}
			spaceId={spaceId}
			mountTimestamp={mountTimestamp}
		/>
	);
};

const SpaceNavigationRelatedContentInner = ({
	spaceId,
	spaceKey,
	cloudId,
	suggestionCountResult,
}: {
	spaceId?: string;
	spaceKey?: string;
	cloudId: string;
	suggestionCountResult: UseFetchSuggestionCountResult;
}): React.JSX.Element | null | undefined => {
	const intl = useIntl();
	const [isVisible, setIsVisible] = useState(true);
	const mountTimestampRef = useRef(Date.now());
	const { dismissAction } = useDismissCuration();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		count,
		isLoading: isLoadingCount,
		isSuppressed,
		refetch: refetchSuggestionCount,
	} = suggestionCountResult;

	if (!spaceKey || !isVisible || isLoadingCount || isSuppressed) {
		return null;
	}

	if (count === 0) {
		return (
			<ThirdPartyAuthenticationEntry
				cloudId={cloudId}
				spaceId={spaceId}
				mountTimestamp={mountTimestampRef.current}
			/>
		);
	}

	if (count > 0) {
		manuallyLogExpExposure('confluence_autonomous_space_curation');
		return (
			<Subscribe to={[FlagsStateContainer]}>
				{(flags: FlagsStateContainer) => (
					<SpaceNavigationRelatedContentBody
						spaceKey={spaceKey}
						mountTimestamp={mountTimestampRef.current}
						suggestionCount={count}
						onDismiss={async () => {
							try {
								await dismissAction({ remindTomorrow: true });
								setIsVisible(false);
								void flags?.showSuccessFlag({
									id: 'space-curation.space-navigation.related-content.remind-tomorrow.success',
									title: intl.formatMessage(i18n.remindTomorrowSuccessTitle),
									description: intl.formatMessage(i18n.remindTomorrowSuccessDescription),
									isAutoDismiss: true,
								});
							} catch {
								// If the dismiss POST fails, keep the card visible so the
								// preference is not silently lost.
								void flags?.showErrorFlag({
									id: 'space-curation.space-navigation.related-content.remind-tomorrow.error',
									title: intl.formatMessage(i18n.dismissErrorTitle),
									description: intl.formatMessage(i18n.dismissErrorDescription),
									isAutoDismiss: true,
								});
							}
						}}
						onNeverShowAgain={async () => {
							try {
								await dismissAction({ neverShowAgain: true });
								setIsVisible(false);
								const neverShowAgainSuccessFlagId =
									'space-curation.space-navigation.related-content.never-show-again.success';
								void flags?.showSuccessFlag({
									id: neverShowAgainSuccessFlagId,
									title: intl.formatMessage(i18n.neverShowAgainSuccessTitle),
									description: intl.formatMessage(i18n.neverShowAgainSuccessDescription),
									actions: [
										{
											content: intl.formatMessage(i18n.undoAction),
											onClick: async () => {
												try {
													await dismissAction({ undoNeverShowAgain: true });
													setIsVisible(true);
													refetchSuggestionCount();
													createAnalyticsEvent({
														type: 'sendUIEvent',
														data: {
															action: 'undoDismiss',
															actionSubject: 'curationEntry',
															source: 'containerNavigation',
															attributes: {
																spaceId,
																spaceKey,
																entryPoint: 'suggestionsEntry',
																dismissMethod: 'never_show_again',
															},
														},
													}).fire();
													void flags?.hideFlag(neverShowAgainSuccessFlagId);
												} catch {
													void flags?.showErrorFlag({
														id: 'space-curation.space-navigation.related-content.never-show-again.undo.error',
														title: intl.formatMessage(i18n.dismissErrorTitle),
														description: intl.formatMessage(i18n.dismissErrorDescription),
														isAutoDismiss: true,
													});
												}
											},
										},
									],
									// No auto-dismiss so the Undo link stays actionable.
									isAutoDismiss: false,
								});
							} catch {
								// If the never-show-again POST fails, keep the card visible so
								// the preference is not silently lost.
								void flags?.showErrorFlag({
									id: 'space-curation.space-navigation.related-content.never-show-again.error',
									title: intl.formatMessage(i18n.dismissErrorTitle),
									description: intl.formatMessage(i18n.dismissErrorDescription),
									isAutoDismiss: true,
								});
							}
						}}
						onSaveSuccessRefetch={refetchSuggestionCount}
						onModalClose={(hadRejections: boolean) => {
							if (hadRejections) {
								refetchSuggestionCount();
							}
						}}
						spaceId={spaceId}
					/>
				)}
			</Subscribe>
		);
	}
};
